import { elementsToCover } from './elementsEarthRules';

// Reglas para combinar los Elements Agua, IA y Tierra de varios contratos en la renovación de la suscripción.
// Todos los cálculos parten del precio total sin redondear y de lo que hay puesto en cada input,
// igual que valora el backend (suma de todos los Elements enviados, redondeada al céntimo).
// La flecha de "rellenar" y la escritura a mano comparten el mismo máximo: lo que falta por cubrir.

export type TRenewalElementType = "water" | "ia" | "earth";

/** Elements puestos en cada input, por tipo y por contrato (mismo índice que subscriptionInfo). */
export type TRenewalElementInputs = Record<TRenewalElementType, number[]>;

/** Precio en euros de un Element de cada tipo. */
export type TRenewalElementPrices = Record<TRenewalElementType, number>;

const ElementTypes: TRenewalElementType[] = ["water", "ia", "earth"];

/**
 * Valor en euros de todos los Elements puestos sin contar los de un input (un tipo de un contrato).
 */
export const otherElementsValue = (inputs: TRenewalElementInputs, prices: TRenewalElementPrices, type: TRenewalElementType, index: number): number =>
{
    return ElementTypes.reduce((total, elementType) =>
    {
        const values = inputs[elementType] ?? [];
        const typeValue = values.reduce((acc, value, valueIndex) =>
        {
            if(elementType === type && valueIndex === index)
            {
                return acc;
            }

            return acc + ((value || 0) * prices[elementType]);
        }, 0);

        return total + typeValue;
    }, 0);
};

/**
 * Elements que necesita un input para cubrir lo que queda del precio contando todos los demás Elements puestos
 * (de cualquier tipo y contrato). Se redondea hacia arriba, así que puede pasar del precio menos de un Element:
 * es el sobrante mínimo para cubrirlo, y el backend lo acepta.
 */
export const elementsNeeded = (totalPrice: number, inputs: TRenewalElementInputs, prices: TRenewalElementPrices, type: TRenewalElementType, index: number): number =>
{
    const remaining = totalPrice - otherElementsValue(inputs, prices, type, index);
    return elementsToCover(remaining, prices[type]);
};

/**
 * Elements que pone la flecha de "rellenar" en un input: los que necesita, sin pasar del saldo del contrato.
 * Los Elements que ya tuviera ese input se sustituyen, no se suman.
 */
export const elementsToFill = (totalPrice: number, inputs: TRenewalElementInputs, prices: TRenewalElementPrices, type: TRenewalElementType, index: number, available: number): number =>
{
    if(!(available > 0))
    {
        return 0;
    }

    return Math.min(available, elementsNeeded(totalPrice, inputs, prices, type, index));
};

/**
 * True si `value` Elements escritos a mano en un input superan los que pondría la flecha de "rellenar"
 * (sin contar el saldo, que se comprueba aparte).
 */
export const exceedsElementsNeeded = (totalPrice: number, inputs: TRenewalElementInputs, prices: TRenewalElementPrices, type: TRenewalElementType, index: number, value: number): boolean =>
{
    return value > elementsNeeded(totalPrice, inputs, prices, type, index);
};
